import BlockData from '../models/BlockData';
import BlockType from '../models/BlockType';

export default class DataManager {
  timeline: Array<Array<BlockData>> = [];
  initialBlocksCounter = 0;

  resetUndo() {
    this.initialBlocksCounter = this.timeline.length;
  }

  loadBlocks(blocks: Array<BlockData>) {
    for (let i = 0; i < blocks.length; i++) {
      this.addBlock(blocks[i]);
    }

    this.initialBlocksCounter = this.timeline.length;
  }

  addBlock(block: BlockData, index?: number) {
    const theCopy = JSON.parse(JSON.stringify(block));

    if (this.timeline.length === 0) {
      this.timeline.push([theCopy]);
      return;
    }

    const current = this.getCurrent();

    if (current.some((c) => c.id === theCopy.id)) {
      return;
    }

    switch (block.type) {
      case BlockType.HEADER:
      case BlockType.HEADER_WITH_LOGO:
      case BlockType.HEADER_LOGO_MENU:
        if (this.isSetHeader()) { return; }
        current.unshift(theCopy);
        break;
      case BlockType.FOOTER:
        if (this.isSetFooter()) { return; }
        current.push(theCopy);
        break;
      default:
        if (index == null) {
          if (this.isSetFooter()) {
            current.splice(current.length - 1, 0, theCopy);
          } else {
            current.push(theCopy);
          }
          break;
        }

        current.splice(index, 0, theCopy);
    }

    this.timeline.push(current);
  }

  getCurrent(): Array<BlockData> {
    if (this.timeline.length === 0) {
      return [];
    }
    return JSON.parse(JSON.stringify(this.timeline[this.timeline.length - 1]));
  }

  undo() {
    if (this.timeline.length === 0) {
      return;
    }

    this.timeline.pop();
  }

  canUndo(): boolean {
    return (this.timeline.length > this.initialBlocksCounter);
  }

  moveUp(id: string) {
    const current = this.getCurrent();
    if (current.length <= 1) { return; }

    const idx = current.findIndex((e) => e.id === id);
    if (idx <= 0) { return; }

    if (this.isSetHeader() && idx <= 1) { return; }
    if (this.isSetFooter() && idx === current.length - 1) { return; }

    const removed = current.splice(idx, 1);
    current.splice(idx - 1, 0, removed[0]);
    this.timeline.push(current);
  }

  moveDown(id: string) {
    const current = this.getCurrent();
    if (current.length <= 1) { return; }

    const idx = current.findIndex((e) => e.id === id);
    if (idx === -1 || idx === current.length - 1) { return; }

    if (this.isSetHeader() && idx <= 0) { return; }
    if (this.isSetFooter() && idx >= current.length - 2) { return; }

    const removed = current.splice(idx, 1);
    current.splice(idx + 1, 0, removed[0]);
    this.timeline.push(current);
  }

  moveTop(id: string) {
    const current = this.getCurrent();
    if (current.length <= 1) { return; }

    const idx = current.findIndex((e) => e.id === id);
    if (idx <= 0) { return; }

    if (this.isSetHeader() && idx <= 1) { return; }
    if (this.isSetFooter() && idx === current.length - 1) { return; }

    let topIdx = 0;
    if (this.isSetFooter()) { topIdx = 1; }

    const removed = current.splice(idx, 1);
    current.splice(topIdx, 0, removed[0]);
    this.timeline.push(current);
  }

  moveBottom(id: string) {
    const current = this.getCurrent();
    if (current.length <= 1) { return; }

    const idx = current.findIndex((e) => e.id === id);
    if (idx === -1 || idx === current.length - 1) { return; }

    if (this.isSetHeader() && idx <= 0) { return; }
    if (this.isSetFooter() && idx >= current.length - 2) { return; }

    let bottomIdx = current.length;
    if (this.isSetFooter()) {
      bottomIdx = current.length - 1;
    }

    const removed = current.splice(idx, 1);
    current.splice(bottomIdx, 0, removed[0]);
    this.timeline.push(current);
  }

  duplicate(id: string) {
    const current = this.getCurrent();
    if (current.length === 0) { return; }

    const idx = current.findIndex((e) => e.id === id);
    if (idx === -1) { return; }

    const target = current[idx];

    switch (target.type) {
      case BlockType.HEADER:
      case BlockType.HEADER_WITH_LOGO:
      case BlockType.HEADER_LOGO_MENU:
      case BlockType.FOOTER:
        return;
      default:
    }

    const theCopy = JSON.parse(JSON.stringify(BlockData.make(target.type, target.data)));
    current.splice(idx + 1, 0, theCopy);
    this.timeline.push(current);
  }

  delete(id: string) {
    const current = this.getCurrent();
    if (current.length === 0) { return; }

    const idx = current.findIndex((e) => e.id === id);
    if (idx === -1) { return; }

    current.splice(idx, 1);

    this.timeline.push(current);
  }

  updateData(id: string, data: any) {
    const current = this.getCurrent();
    if (current.length === 0) { return; }

    const idx = current.findIndex((e) => e.id === id);
    if (idx === -1) { return; }

    const target = current[idx];
    target.data = JSON.parse(JSON.stringify(data));
    current.splice(idx, 1, target);
    this.timeline.push(current);
  }

  isSetHeader(): boolean {
    const current = this.getCurrent();

    if (current.length <= 0) { return false; }

    if (current[0].type === BlockType.HEADER) { return true; }
    if (current[0].type === BlockType.HEADER_WITH_LOGO) { return true; }
    if (current[0].type === BlockType.HEADER_LOGO_MENU) { return true; }
    return false;
  }

  isSetFooter(): boolean {
    const current = this.getCurrent();
    if (current.length <= 0) { return false; }

    if (current[current.length - 1].type === BlockType.FOOTER) { return true; }
    return false;
  }

  reset() {
    this.timeline = [];
  }
}
